import { ChatConfig, Thread, Message, MessageContent } from '../types';

// Debug flag - set to false to minimize logging
const DEBUG_MODE = false;

// Create a modifiable debug flag that we can toggle for specific functions
let currentDebugMode = DEBUG_MODE;

// Logger utility for consistent and controlled logging
const logger = {
  debug: (message: string, ...args: any[]) => {
    if (currentDebugMode) {
      console.log(`[MattressAI Debug] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]) => {
    if (currentDebugMode) {
      console.log(`[MattressAI] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`[MattressAI Warning] ${message}`, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(`[MattressAI Error] ${message}`, ...args);
  },
  // Function to temporarily enable debug mode
  enableDebug: () => {
    const previous = currentDebugMode;
    currentDebugMode = true;
    return previous;
  },
  // Function to restore previous debug mode
  restoreDebug: (previous: boolean) => {
    currentDebugMode = previous;
  }
};

// Define the worker URL selection logic
function getWorkerUrl() {
  // Get URL parameters for debugging and testing
  const urlParams = new URLSearchParams(window.location.search);
  const forceEndpoint = urlParams.get('endpoint') || localStorage.getItem('forceEndpoint');
  
  // If a specific endpoint is forced via query param, use it (for debugging)
  if (forceEndpoint) {
    logger.debug('Using override API endpoint from URL param:', forceEndpoint);
    return forceEndpoint;
  }
  
  // Check if we're working with a subdomain of chat.mattressai.co
  const hostname = window.location.hostname;
  if (hostname.includes('.chat.mattressai.co')) {
    // For production subdomains, prefer the api.mattressai.co endpoint
    logger.debug('Using primary API endpoint for chat subdomain');
    return 'https://api.mattressai.co';
  }
  
  // For development or other environments, try direct worker endpoint first
  // as it's more likely to work in local testing
  if (hostname === 'localhost' || hostname.includes('127.0.0.1') || hostname.includes('pages.dev')) {
    logger.debug('Using direct worker endpoint for development');
    return 'https://mattressai2-chat.blake-dac.workers.dev';
  }
  
  // Default to production API
  logger.debug('Using production API endpoint: https://api.mattressai.co');
  return 'https://api.mattressai.co';
}

// Use the dynamically selected worker URL
const WORKER_URL = getWorkerUrl();
logger.debug('Using API endpoint:', WORKER_URL);

// Add backup endpoints in case the primary fails
const BACKUP_ENDPOINTS = [
  'https://chat-api.mattressai.co'
];

// Add a cache-busting version parameter
const VERSION = 'v=' + Date.now();

// Constant for domains that should use no-cors mode
const FORCE_NO_CORS_DOMAINS: (string | RegExp)[] = [];

// Add a function to test CORS connectivity
export async function testApiConnectivity(): Promise<boolean> {
  try {
    const testUrl = `${WORKER_URL}/api/test?${VERSION}`;
    logger.debug('Testing API connectivity:', testUrl);
    
    const response = await fetch(testUrl, {
      method: 'GET',
      mode: 'no-cors', // Use no-cors mode to bypass CORS restriction
      headers: {
        'Accept': 'application/json'
      }
    });
    
    // In no-cors mode, we'll always get an opaque response (status=0)
    // So we just check if we got a response at all
    logger.debug('API connectivity test response:', { 
      status: response.status, 
      type: response.type,
      ok: response.ok
    });
    
    // If we get here without throwing, we consider it a success
    return true;
  } catch (error) {
    logger.error('API connectivity test error:', error);
    return false;
  }
}

// For debugging purposes - this will show up in the console
logger.debug('Current window.location:', {
  hostname: window.location.hostname,
  href: window.location.href,
  origin: window.location.origin
});

// Store the original hostname for comparison
const ORIGINAL_HOSTNAME = window.location.hostname;

interface ChatMessage {
  content: string;
  threadId?: string | null;
}

// Updated to handle tool calls and the new API response format
export interface ChatResponse {
  content?: string | MessageContent;
  message?: string | MessageContent;  // Add message field as an alternative to content
  role: 'assistant';
  threadId: string;
  isNewThread?: boolean;
  sessionId?: string;
}

// Add a cache for config data
let configCache: {
  data: ChatConfig | null;
  timestamp: number;
  hostname: string;
} = {
  data: null,
  timestamp: 0,
  hostname: ''
};

// Cache expiration time (2 minutes) - setting to 0 to disable caching
const CONFIG_CACHE_TTL = 0; // Disabled caching

/**
 * Common fetch wrapper with error handling, retry logic, and endpoint failover
 */
async function apiFetch<T>(url: string, options?: RequestInit, retries = 3): Promise<T> {
  let lastError: Error | null = null;
  const backoff = (attempt: number) => 1000 * Math.pow(2, attempt - 1);
  
  // Add CORS mode to all requests
  const fetchOptions: RequestInit = {
    ...options,
    mode: 'cors',
    credentials: 'include',
  };
  
  // First try the primary endpoint
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      logger.debug(`Attempt ${attempt + 1}/${retries} for ${url}`);
      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const text = await response.text();
      
      try {
        const parsed = JSON.parse(text) as T;
        logger.debug(`Response from ${url}:`, parsed);
        return parsed;
      } catch (parseError) {
        logger.error(`Invalid JSON response from ${url}:`, text.substring(0, 200) + '...');
        throw new Error(`Invalid JSON response: ${parseError}`);
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      logger.warn(`Attempt ${attempt + 1} failed for ${url}:`, lastError.message);
      
      if (attempt < retries - 1) {
        const delay = backoff(attempt + 1);
        logger.debug(`Retrying after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If primary endpoint fails, try backup endpoints
  if (url.startsWith(WORKER_URL)) {
    const path = url.substring(WORKER_URL.length);
    
    for (const backupEndpoint of BACKUP_ENDPOINTS) {
      if (backupEndpoint === WORKER_URL) continue; // Skip if same as primary
      
      const backupUrl = `${backupEndpoint}${path}`;
      logger.warn(`Primary endpoint failed, trying backup: ${backupUrl}`);
      
      try {
        const response = await fetch(backupUrl, fetchOptions);
        
        if (!response.ok) {
          logger.warn(`Backup endpoint ${backupUrl} returned ${response.status}`);
          continue; // Try next backup
        }
        
        const text = await response.text();
        
        try {
          return JSON.parse(text) as T;
        } catch (parseError: unknown) {
          logger.warn(`Invalid JSON from backup endpoint ${backupUrl}: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
          continue; // Try next backup
        }
      } catch (error: unknown) {
        logger.warn(`Error from backup endpoint ${backupUrl}: ${error instanceof Error ? error.message : String(error)}`);
        continue; // Try next backup
      }
    }
  }
  
  // If we got here, all endpoints failed
  throw lastError || new Error('Request failed after multiple attempts');
}

/**
 * Process response content to handle various formats
 */
function processResponseContent(data: any): string | MessageContent {
  // Handle contentStr field which may contain product recommendations
  if (data.contentStr) {
    try {
      return JSON.parse(data.contentStr) as MessageContent;
    } catch {
      // Fall back to regular content/message
      return data.content || data.message;
    }
  }
  
  // Use the normal content field
  let processedContent = data.content || data.message;
  
  // Handle the case where content is a stringified JSON with product recommendations
  if (typeof processedContent === 'string' && 
      processedContent.includes('"type":"product_recommendations"') &&
      processedContent.includes('"message_type":"PRODUCT_RECOMMENDATIONS"')) {
    try {
      return JSON.parse(processedContent) as MessageContent;
    } catch {
      // Keep the original string if parsing fails
      return processedContent;
    }
  }
  
  return processedContent;
}

/**
 * Helper function to properly determine the hostname parameter to send to the API
 */
function getRequestHostname(): string {
  const hostname = window.location.hostname;
  const urlParams = new URLSearchParams(window.location.search);
  const debugMerchant = urlParams.get('merchant');
  const debugSubdomain = urlParams.get('subdomain');
  
  // Case 1: URL explicitly specifies a subdomain for testing
  if (debugSubdomain) {
    logger.debug(`Using debug subdomain from URL: ${debugSubdomain}`);
    return `${debugSubdomain}.chat.mattressai.co`;
  }
  
  // Case 2: Already on a .chat.mattressai.co domain - use the actual hostname
  if (hostname.includes('.chat.mattressai.co')) {
    logger.debug(`Using actual .chat.mattressai.co hostname: ${hostname}`);
    return hostname;
  }
  
  // Case 3: URL has a specific merchant parameter
  if (debugMerchant) {
    logger.debug(`Using debug merchant from URL parameter: ${debugMerchant}`);
    return `${debugMerchant}.chat.mattressai.co`;
  }
  
  // Case 4: Development environment with special hostname formats
  if (hostname === 'localhost' || hostname.includes('pages.dev')) {
    // Check localStorage for recent subdomain
    const storedSubdomain = localStorage.getItem('lastSubdomain');
    if (storedSubdomain) {
      logger.debug(`Using stored subdomain from localStorage: ${storedSubdomain}`);
      return `${storedSubdomain}.chat.mattressai.co`;
    }
    
    // Fall back to demo for development
    logger.debug('Development environment detected, using demo merchant');
    return 'demo.chat.mattressai.co';
  }
  
  // Case 5: Try to use the first segment of the hostname as subdomain
  const firstSegment = hostname.split('.')[0];
  if (firstSegment && firstSegment !== 'www' && firstSegment !== 'mattressai2-chat') {
    logger.debug(`Using first hostname segment as subdomain: ${firstSegment}`);
    return `${firstSegment}.chat.mattressai.co`;
  }
  
  // Final fallback: Use the full hostname
  logger.debug(`No subdomain found, using full hostname: ${hostname}`);
  return hostname;
}

/**
 * Extract merchant ID from the hostname
 */
function getMerchantIdFromHostname(hostname: string): string {
  const urlParams = new URLSearchParams(window.location.search);
  const debugMerchant = urlParams.get('merchant');
  
  if (hostname.includes('.chat.mattressai.co')) {
    return hostname.split('.chat.mattressai.co')[0];
  } else if (debugMerchant) {
    return debugMerchant;
  } else {
    const merchantId = hostname.split('.')[0];
    return merchantId;
  }
}

/**
 * Fetches configuration for the chat from the chat worker
 */
export async function fetchConfig(): Promise<ChatConfig> {
  // Get URL parameters
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  
  // Try to get subdomain for merchant identification
  let hostname = getRequestHostname();
  
  // Use the forceEndpoint flag for debugging if present
  const forceEndpoint = params.get('forceEndpoint') || localStorage.getItem('forceEndpoint');
  
  // Get worker URL - now using a dedicated function to allow for overrides
  const workerUrl = getWorkerUrl();
  
  // Build the API URL with proper parameters
  let apiUrl = `${workerUrl}/api/config?hostname=${encodeURIComponent(hostname)}`;
  
  // Add timestamp to prevent caching
  apiUrl += `&v=${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  
  // Log the URL we're using
  logger.debug('Fetching config from:', { 
    apiUrl, 
    originalHostname: window.location.hostname,
    processedHostname: hostname
  });
  
  // Detect if we should use no-cors mode for this domain
  const shouldUseNoCors = FORCE_NO_CORS_DOMAINS.some((domain: string | RegExp) => {
    if (typeof domain === 'string') {
      return hostname.includes(domain);
    }
    return domain.test(hostname);
  });
  
  if (shouldUseNoCors) {
    logger.debug('Using no-cors mode directly for this domain');
    try {
      // Try with no-cors mode
      await fetch(url, { 
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      
      // If we get here, the request didn't throw, but we can't access the response
      // with no-cors mode, so return default config
      logger.info('Used no-cors mode, returning default config');
      return getDefaultConfig(hostname);
    } catch (e) {
      logger.error('Error fetching config with no-cors:', e);
      return getDefaultConfig(hostname);
    }
  }
  
  // Otherwise, try the regular approach with fallback
  try {
    // First try with regular CORS mode
    const response = await apiFetch<any>(apiUrl);
    
    // NEW API returns structure: { success: true, data: { merchantConfig: {...}, assistantConfig: { threadId: "..." } } }
    if (response && response.success && response.data) {
      // Log the full response structure for debugging
      logger.debug('Raw API response structure:', {
        keys: Object.keys(response),
        dataKeys: response.data ? Object.keys(response.data) : 'no data',
        hasAssistantConfig: response.data && 'assistantConfig' in response.data,
        threadIdValue: response.data?.assistantConfig?.threadId,
        hasMerchantConfig: response.data && 'merchantConfig' in response.data
      });
      
      // Handle the updated response format with merchantConfig and assistantConfig
      if (response.data.merchantConfig) {
        const config = response.data.merchantConfig;
        const threadId = response.data.assistantConfig?.threadId;
        
        logger.debug('Successfully retrieved config from updated API structure', {
          dataKeys: Object.keys(response.data),
          configKeys: Object.keys(config),
          threadId
        });
        
        // Add the threadId to the returned data for the chat store to access
        return {
          ...config,
          thread: threadId // Add thread property for backward compatibility
        };
      } else if (response.data.config) {
        // Legacy format support for older API versions
        logger.debug('Retrieved config from API (legacy format with data.config)');
        return response.data;
      } else {
        // Direct response compatibility for even older API versions
        logger.debug('Retrieved config directly from response.data (oldest format)');
        return response.data;
      }
    }
    
    // General fallback for legacy format or unexpected formats
    logger.warn('Unexpected API response format, using response directly:', {
      responseType: typeof response,
      hasSuccess: response && 'success' in response,
      hasData: response && 'data' in response
    });
    
    // As a fallback, directly use the response
    return response;
    
  } catch (error) {
    // Log the error
    logger.error('Error fetching config:', error);
    
    // Return default config on error
    return getDefaultConfig(hostname);
  }
}

// Helper function to get a default configuration
function getDefaultConfig(hostname: string): ChatConfig {
  // Extract subdomain from hostname if it's a .chat.mattressai.co domain
  let subdomain = '';
  if (hostname.includes('.chat.mattressai.co')) {
    subdomain = hostname.split('.chat.mattressai.co')[0];
  } else {
    subdomain = hostname.split('.')[0];
  }
  
  // Create a user-friendly store name
  const storeName = subdomain 
    ? `${subdomain.charAt(0).toUpperCase()}${subdomain.slice(1).replace(/-/g, ' ')} Store`
    : 'Mattress Store';
  
  // Default primary color
  const primaryColor = '#3B82F6';
  
  return {
    merchantId: 'default', // The actual merchantId will be determined by the worker
    name: storeName,
    storeInfo: {
      name: storeName,
      hours: '9am-5pm EST Monday-Friday',
      hasMultipleLocations: false,
      contactInfo: subdomain ? `support@${subdomain.replace(/-/g, '')}.com` : 'support@mattressai.co',
    },
    branding: {
      colors: {
        primary: primaryColor,
        secondary: '#1E40AF',
        background: '#FFFFFF',
        text: '#1F2937',
      },
      fonts: {
        primary: 'Inter',
        secondary: 'Roboto',
      },
      logo: 'https://mattressai.co/logo.png',
      chatTitle: 'Mattress Assistant',
    },
    chatConfig: {
      initialGreeting: `Welcome to ${storeName}! I'm here to help you find the perfect mattress. What brings you in today?`,
      welcomeMessage: "I'm your mattress AI assistant. How can I help you today?",
      masterPrompt: '',
      temperature: 0.7,
      maxTokens: 1000,
      model: 'gpt-4o',
      assistantId: '',
      toneDirective: 'friendly and helpful',
      conversationStyle: 'casual',
      leadCaptureTiming: 'when-relevant',
      primaryGoal: 'product-recommendation',
      responseCreativity: 0.7,
      productRecommendationLimit: 3,
    },
    features: {
      chatEnabled: true,
      recommendationsEnabled: true,
      analyticsEnabled: true,
    },
    subscription: {
      plan: 'free',
      status: 'active',
      features: ['basic-chat', 'basic-analytics'],
      limits: {
        monthlyConversations: 100,
        concurrentChats: 3,
        retentionDays: 30,
      },
    },
  } as unknown as ChatConfig; // Use type assertion to avoid TypeScript errors
}

/**
 * Calculate a simple hash for the master prompt to identify unique prompts
 */
function calculateMasterPromptHash(prompt: string): string {
  // Simple hashing algorithm - just for identifying unique prompts
  // Convert the string to a number hash
  let hash = 0;
  if (prompt.length === 0) return 'empty-prompt';
  
  for (let i = 0; i < prompt.length; i++) {
    const char = prompt.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert to hex string and add a prefix
  return 'prompt-' + Math.abs(hash).toString(16).substring(0, 8);
}

/**
 * Sends a message to the chat API
 * @param content The message to send
 * @param threadId Optional thread ID for continuing conversations
 * @param sessionId Optional session ID for tracking sessions
 * @param metadata Additional metadata to send with the message
 * @returns The API response with the assistant's reply
 */
export const sendChatMessage = async (
  content: string, 
  threadId?: string, 
  sessionId?: string, 
  metadata?: any
): Promise<ChatResponse> => {
  // Get the appropriate hostname for merchant resolution
  const requestHostname = getRequestHostname();
  
  logger.debug('Sending message with hostname:', requestHostname);

  // Create the URL with hostname parameter only - let the worker resolve the merchantId
  const urlWithParams = `${WORKER_URL}/api/chat/messages?hostname=${encodeURIComponent(requestHostname)}`;
  
  // If no sessionId provided, we need to create a session first
  if (!sessionId) {
    logger.info('No sessionId provided, creating a session first');
    
    try {
      // Get the merchantId from hostname
      const merchantId = getMerchantIdFromHostname(requestHostname);
      
      // Create a session
      const sessionUrl = `${WORKER_URL}/api/session`;
      const sessionResult = await apiFetch<any>(sessionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Hostname': requestHostname
        },
        body: JSON.stringify({
          merchantId,
          customerInfo: {
            hostname: requestHostname,
            origin: window.location.origin
          }
        })
      });
      
      // Extract sessionId and threadId
      if (sessionResult && sessionResult.data) {
        logger.info('Successfully created session', sessionResult.data);
        sessionId = sessionResult.data.sessionId;
        // If threadId wasn't provided, use the one from session creation
        if (!threadId && sessionResult.data.threadId) {
          threadId = sessionResult.data.threadId;
        }
      } else {
        logger.error('Failed to create session', sessionResult);
        throw new Error('Failed to create session');
      }
    } catch (error) {
      logger.error('Error creating session', error);
      throw new Error('Failed to send message: session creation failed');
    }
  }
  
  // Create the request body
  const requestData = {
    content,
    threadId: threadId || null,
    sessionId: sessionId, // Now guaranteed to have a value
    timestamp: new Date().toISOString(),
    clientConfig: {
      hostname: requestHostname,
      origin: window.location.origin
    },
    metadata
  };
  
  const data = await apiFetch<any>(urlWithParams, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Hostname': requestHostname
    },
    body: JSON.stringify(requestData)
  });
  
  // Process the content from the response
  const processedContent = processResponseContent(data);
  
  // Format the final response
  return {
    content: processedContent,
    role: 'assistant',
    threadId: data.threadId,
    isNewThread: !threadId && !!data.threadId,
    sessionId: sessionId as string,
  };
};

// Export sendChatMessage as sendMessage for backward compatibility
export const sendMessage = sendChatMessage;

export async function getThread(threadId: string): Promise<Thread> {
  const url = `${WORKER_URL}/api/chat/threads/${threadId}`;
  return apiFetch<Thread>(url);
}

/**
 * Response type for session ending
 */
interface SessionEndResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  status?: 'pending' | 'completed' | 'failed';
}

/**
 * Ends a chat session by calling the worker endpoint
 * Improved implementation with retry logic and handling of specific error codes
 */
export async function endSession(
  sessionId: string, 
  threadId: string, 
  merchantId: string, 
  lastActivityTime?: string,
  endReason: string = 'user_closed'
): Promise<SessionEndResponse> {
  const url = `${WORKER_URL}/api/chat/sessions/end`;
  
  // Prepare the request payload
  const payload = {
    sessionId,
    threadId,
    merchantId,
    lastActivityTime: lastActivityTime || new Date().toISOString(),
    completionReason: endReason
  };

  logger.info(`Ending session ${sessionId} with reason: ${endReason}`);
  
  // Configure retry options
  const maxRetries = 3;
  const retryDelay = 1000; // Start with 1 second
  let attempt = 0;
  
  while (attempt < maxRetries) {
    attempt++;
    
    try {
      // Use apiFetch with standard options
      const response = await apiFetch<SessionEndResponse>(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      // If we get here, the request was successful
      logger.info(`Session ${sessionId} ended successfully via apiFetch (attempt ${attempt})`);
      
      // Return the structured response
      return response;
      
    } catch (error: any) { // Type as any to access properties
      // Check if it's a network or server error that should be retried
      const isServerError = 
        (error && error.message && 
          (error.message.includes('500') || 
           error.message.includes('503') || 
           error.message.includes('timeout'))) ||
        error instanceof TypeError; // Network errors are typically TypeError
      
      // Only retry server errors, not client errors
      if (isServerError && attempt < maxRetries) {
        // Exponential backoff
        const backoffTime = retryDelay * Math.pow(2, attempt - 1);
        logger.warn(`Retrying session end after ${backoffTime}ms (attempt ${attempt}/${maxRetries})`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, backoffTime));
        continue;
      }
      
      // Log the specific error from apiFetch
      logger.error(`Error ending session ${sessionId} via apiFetch (attempt ${attempt}/${maxRetries}):`, error);
      
      // Structure the error result
      return {
        success: false,
        error: error && error.message ? error.message : String(error),
        code: 'CLIENT_ERROR'
      };
    }
  }
  
  // If we get here, all retries failed
  logger.error(`Failed to end session ${sessionId} after ${maxRetries} attempts`);
  return {
    success: false,
    error: `Failed to end session after ${maxRetries} attempts`,
    code: 'MAX_RETRIES_EXCEEDED'
  };
}

/**
 * Creates a new chat session
 */
export async function createSession(config: ChatConfig): Promise<string> {
  logger.info('Creating new session', { merchantId: config.merchantId });
  
  // Calculate or provide a default masterPromptHash
  const masterPromptHash = config.chatConfig?.masterPrompt 
    ? calculateMasterPromptHash(config.chatConfig.masterPrompt) 
    : 'default-prompt-hash';
  
  const data = {
    merchantId: config.merchantId,
    masterPromptHash: masterPromptHash,
    customerInfo: {
      referrer: window.location.hostname,
      timestamp: new Date().toISOString()
    }
  };
  
  logger.debug('Session creation data:', data);
  
  // Try multiple endpoints in sequence
  const endpoints = [
    '/api/session',                // Standard endpoint  
    '/api/simple-session',         // Simplified endpoint
    '/api/fallback/session',       // Fallback endpoint
    '/api/test-session-create'     // Test endpoint
  ];
  
  let lastError = null;
  
  // Try each endpoint in order
  for (const endpoint of endpoints) {
    try {
      logger.info(`Trying to create session with endpoint: ${endpoint}`);
      
      const response = await fetch(`${WORKER_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Hostname': window.location.hostname,
          'Origin': window.location.origin
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.warn(`Endpoint ${endpoint} failed: ${response.status} - ${errorText}`);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      logger.info(`Session created successfully with endpoint ${endpoint}`, { sessionId: result.sessionId });
      return result.sessionId;
    } catch (error) {
      lastError = error;
      logger.warn(`Endpoint ${endpoint} failed`, { error: error instanceof Error ? error.message : String(error) });
      // Continue to next endpoint
    }
  }
  
  // If we get here, all endpoints failed
  logger.error('All session creation endpoints failed', { lastError });
  throw lastError;
} 